import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Info, X, Euro, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { Button } from './ui/Button';
interface SlideFastCalculatorProps {
  onContinue: () => void;
}
function formatEuro(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}
interface SliderInputProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
}
function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step = 1
}: SliderInputProps) {
  return <div className="flex flex-col gap-2" data-id="element-441" data-mp="30n548ycxtjb5">
      <div className="flex items-center justify-between" data-id="element-442" data-mp="30jzctcgu65c9">
        <label className="text-sm font-medium text-slate-600" data-id="element-443" data-mp="2mnvlps19ed40">{label}</label>
        <input type="number" value={value} onChange={e => {
        const v = Number(e.target.value);
        if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
      }} className="w-20 text-right text-sm font-semibold text-slate-900 bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#6C2BD9]/30 focus:border-[#6C2BD9]" min={min} max={max} step={step} data-id="element-444" data-mp="2rlx5kf6ieggj" />
      </div>
      <input type="range" value={value} onChange={e => onChange(Number(e.target.value))} min={min} max={max} step={step} className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-[#6C2BD9] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#6C2BD9] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#6C2BD9] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer" data-id="element-445" data-mp="3jlt4tyyc1fjl" />
    </div>;
}
type InfoKey = 'master' | 'localization' | 'insights' | null;
interface InfoPopoverProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  title: string;
  body: string;
}
function InfoPopover({
  isOpen,
  onToggle,
  onClose,
  title,
  body
}: InfoPopoverProps) {
  return <span className="relative inline-flex" data-id="element-446" data-mp="gvaoep95fbyo">
      <button type="button" onClick={onToggle} aria-expanded={isOpen} aria-label={title} className="w-5 h-5 rounded-full bg-[#6C2BD9]/[0.08] hover:bg-[#6C2BD9]/[0.16] border border-[#6C2BD9]/30 text-[#6C2BD9] flex items-center justify-center transition-colors" data-id="element-447" data-mp="lrhow2irzv98">
        <Info className="w-3 h-3" data-id="element-448" data-mp="225hmh6u5f8tb" />
      </button>
      <AnimatePresence data-id="element-449" data-mp="228vlq67in1wj">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.15
      }} className="absolute z-30 top-full left-0 mt-2 w-[280px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-450" data-mp="26736dc53ond0">
            <div className="flex items-start justify-between gap-3" data-id="element-451" data-mp="32zszy2zxshwg">
              <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-452" data-mp="2yy7is5gddhgg">
                {title}
              </p>
              <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-453" data-mp="2u1h4nno4kvxo">
                <X className="w-3.5 h-3.5" data-id="element-454" data-mp="s6owqu5rd5gk" />
              </button>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-455" data-mp="3l0x6hwenryxu">
              {body}
            </p>
          </motion.div>}
      </AnimatePresence>
    </span>;
}
export function SlideFastCalculator({
  onContinue
}: SlideFastCalculatorProps) {
  const [products, setProducts] = useState(1);
  const [campaignsPerProduct, setCampaignsPerProduct] = useState(2);
  const [countries, setCountries] = useState(1);
  const [benchmarkOpen, setBenchmarkOpen] = useState(false);
  const [costBenchmarkOpen, setCostBenchmarkOpen] = useState(false);
  const [unitsOpen, setUnitsOpen] = useState(false);
  const [costOpen, setCostOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState<InfoKey>(null);
  // List-price unit economics
  const masterSnakCostBase = 2900;
  const masterSnakCostFloor = 1900;
  const campaignDuration = 3;
  const localizationCostBase = 999;
  const monthlyInsightsFeeBase = 99;
  const durationRebate = 0;
  const FOLLOW_UP_MASTER_COST = 1000;
  // --- Volume discounts ---
  const COUNTRY_DISCOUNT_FLOOR = 2;
  const COUNTRY_DISCOUNT_CEILING = 50;
  const COUNTRY_DISCOUNT_MAX = 0.5;
  const countryDiscount = Math.min(COUNTRY_DISCOUNT_MAX, Math.max(0, (countries - COUNTRY_DISCOUNT_FLOOR) / (COUNTRY_DISCOUNT_CEILING - COUNTRY_DISCOUNT_FLOOR) * COUNTRY_DISCOUNT_MAX));
  const effectiveCountryCost = localizationCostBase * (1 - countryDiscount);
  const countryDeploymentCost = effectiveCountryCost;
  const totalAnnualCampaigns = products * campaignsPerProduct;
  const MASTER_DISCOUNT_FLOOR = 1;
  const MASTER_DISCOUNT_CEILING = 100;
  const masterProgress = Math.min(1, Math.max(0, (totalAnnualCampaigns - MASTER_DISCOUNT_FLOOR) / (MASTER_DISCOUNT_CEILING - MASTER_DISCOUNT_FLOOR)));
  const effectiveMasterSnakCost = masterSnakCostBase - (masterSnakCostBase - masterSnakCostFloor) * masterProgress;
  const masterSnakCost = effectiveMasterSnakCost;
  const INSIGHTS_DISCOUNT_FLOOR = 2;
  const INSIGHTS_DISCOUNT_CEILING = 50;
  const INSIGHTS_DISCOUNT_MAX = 0.5;
  const insightsDiscount = Math.min(INSIGHTS_DISCOUNT_MAX, Math.max(0, (countries - INSIGHTS_DISCOUNT_FLOOR) / (INSIGHTS_DISCOUNT_CEILING - INSIGHTS_DISCOUNT_FLOOR) * INSIGHTS_DISCOUNT_MAX));
  const effectiveMonthlyInsightsFee = monthlyInsightsFeeBase * (1 - insightsDiscount);
  const monthlyInsightsFee = effectiveMonthlyInsightsFee;
  const calc = useMemo(() => {
    const totalCampaigns = products * campaignsPerProduct;
    const hasMaster = masterSnakCost > 0;
    const firstCampaigns = hasMaster ? products : 0;
    const followUpCampaigns = hasMaster ? Math.max(0, totalCampaigns - products) : 0;
    const annualMasterSnakCost = hasMaster ? firstCampaigns * masterSnakCost + followUpCampaigns * FOLLOW_UP_MASTER_COST : 0;
    const deploymentPerCampaign = countryDeploymentCost * countries;
    const insightsPerCampaign = monthlyInsightsFee * campaignDuration * countries;
    const rawCostFirstCampaign = masterSnakCost + deploymentPerCampaign + insightsPerCampaign;
    const rawCostFollowUp = FOLLOW_UP_MASTER_COST + deploymentPerCampaign + insightsPerCampaign;
    const rawCostRollout = deploymentPerCampaign + insightsPerCampaign;
    const rawCostPerCampaign = hasMaster ? rawCostFirstCampaign : rawCostRollout;
    const totalBeforeRebate = hasMaster ? firstCampaigns * rawCostFirstCampaign + followUpCampaigns * rawCostFollowUp : totalCampaigns * rawCostRollout;
    const totalAnnualCost = totalBeforeRebate * (1 - durationRebate);
    const costPerCampaign = totalCampaigns > 0 ? totalAnnualCost / totalCampaigns : 0;
    const annualDeploymentCost = totalCampaigns * deploymentPerCampaign;
    const annualInsightsCost = totalCampaigns * insightsPerCampaign;
    const totalDeployments = totalCampaigns * countries;
    const costPerDeployment = totalDeployments > 0 ? totalAnnualCost / totalDeployments : 0;
    return {
      totalCampaigns,
      totalDeployments,
      firstCampaigns,
      followUpCampaigns,
      rawCostPerCampaign,
      costPerCampaign,
      costPerDeployment,
      totalAnnualCost,
      annualMasterSnakCost,
      annualDeploymentCost,
      annualInsightsCost
    };
  }, [products, campaignsPerProduct, countries, masterSnakCost, countryDeploymentCost, monthlyInsightsFee, campaignDuration]);
  return <div className="h-full w-full overflow-y-auto bg-white text-slate-900 font-sans" data-id="element-456" data-mp="2b7ipvtcow79r">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 md:py-8" data-id="element-457" data-mp="1wiuz00dsglq5">
        {/* Header */}
        <div className="mb-6" data-id="element-458" data-mp="21gwgwhzhaf01">
          <div className="flex items-center gap-3 mb-1" data-id="element-459" data-mp="21g4jr2q8g97n">
            <span className="text-2xl font-bold tracking-tight text-[#6C2BD9]" data-id="element-460" data-mp="2p79zzj0353ih">
              Snakker
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight" data-id="element-461" data-mp="2u0ux78tuttgm">
            See for yourself — move the sliders to see the{' '}
            <span className="text-[#6C2BD9]" data-id="element-462" data-mp="djx1vdk7fjz8">potential</span>
          </h1>


          {/* Industry benchmark + Cost benchmark popovers */}
          <div className="mt-3 flex flex-wrap items-start gap-2" data-id="element-463" data-mp="sbfgp81bv8h0">
            <div className="relative inline-block" data-id="element-464" data-mp="nb1fa8u8ewry">
              <button type="button" onClick={() => {
              setBenchmarkOpen(v => !v);
              setCostBenchmarkOpen(false);
            }} aria-expanded={benchmarkOpen} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#6C2BD9]/[0.07] hover:bg-[#6C2BD9]/[0.12] border border-[#6C2BD9]/20 text-xs font-semibold text-[#6C2BD9] transition-colors" data-id="element-465" data-mp="3vpt8nku9vvax">
                <Info className="w-3.5 h-3.5" data-id="element-466" data-mp="1k2mxtv93cs62" />
                Industry benchmark
              </button>
              <AnimatePresence data-id="element-467" data-mp="ibc288yac3ct">
                {benchmarkOpen && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} transition={{
                duration: 0.15
              }} className="absolute z-20 mt-2 w-[320px] md:w-[380px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-468" data-mp="iak4osl4nvjw">
                    <div className="flex items-start justify-between gap-3" data-id="element-469" data-mp="1kwvozqaimelf">
                      <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-470" data-mp="1fyu6b6h04hah">
                        Big Pharma at a glance
                      </p>
                      <button type="button" onClick={() => setBenchmarkOpen(false)} aria-label="Close benchmark" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-471" data-mp="1shg4hpzxvx4r">
                        <X className="w-3.5 h-3.5" data-id="element-472" data-mp="14kaeq2yg2g4s" />
                      </button>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-473" data-mp="19i26mwz3a28z">
                      Big pharma typically runs{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-474" data-mp="1egdifd8y2mtu">
                        20–60 brands
                      </span>{' '}
                      ×{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-475" data-mp="2pxvavgl2s012">
                        100–135 countries
                      </span>{' '}
                      ×{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-476" data-mp="utox29khmyo3">
                        4–6 campaigns/year
                      </span>
                      .
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div className="relative inline-block" data-id="element-477" data-mp="13s97yojt441r">
              <button type="button" onClick={() => {
              setCostBenchmarkOpen(v => !v);
              setBenchmarkOpen(false);
            }} aria-expanded={costBenchmarkOpen} className="hidden items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#6C2BD9]/[0.07] hover:bg-[#6C2BD9]/[0.12] border border-[#6C2BD9]/20 text-xs font-semibold text-[#6C2BD9] transition-colors" data-id="element-478" data-mp="ywtzkc4kj0cq">
                <Euro className="w-3.5 h-3.5" data-id="element-479" data-mp="3okcf52nt2nyb" />
                Cost benchmark
              </button>
              <AnimatePresence data-id="element-480" data-mp="3jmaw2hqdqomu">
                {costBenchmarkOpen && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} transition={{
                duration: 0.15
              }} className="absolute z-20 mt-2 w-[320px] md:w-[420px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-481" data-mp="1z3qqtcmynbkq">
                    <div className="flex items-start justify-between gap-3" data-id="element-482" data-mp="2dug3kjg2kyqp">
                      <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-483" data-mp="xd22g3kar5xl">
                        Cost Benchmark — Digital Pharma Campaigns
                      </p>
                      <button type="button" onClick={() => setCostBenchmarkOpen(false)} aria-label="Close cost benchmark" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-484" data-mp="1hoo23062elgj">
                        <X className="w-3.5 h-3.5" data-id="element-485" data-mp="13tlpxvamjzva" />
                      </button>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-486" data-mp="20jmnaip27oo7">
                      A typical digital campaign for a legacy brand in one
                      country runs{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-487" data-mp="1rndkcu1vi7xb">
                        €15,000 – €19,000
                      </span>
                      .
                    </p>
                    <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-488" data-mp="g7f9qlr1rdtz">
                      Compare against Snakker's cost per localized deployment.
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-id="element-489" data-mp="602s1sylmnwg">
          {/* Inputs */}
          <div className="space-y-5" data-id="element-490" data-mp="az773087ffup">
            <button type="button" onClick={() => setUnitsOpen(v => !v)} aria-expanded={unitsOpen} className="w-full flex items-center gap-3 rounded-2xl border-2 border-[#6C2BD9]/20 bg-white px-5 py-4 text-left hover:bg-[#6C2BD9]/[0.04] transition-colors" data-id="element-491" data-mp="fsia0q9wbchc">
              <SlidersHorizontal className="w-5 h-5 text-[#6C2BD9] shrink-0" data-id="element-492" data-mp="tt2tzprjrecf" />
              <span className="flex-1 flex flex-wrap items-baseline gap-x-2" data-id="element-493" data-mp="yr4ca8h5f9mu">
                <span className="text-base md:text-lg font-bold text-slate-900 tracking-tight" data-id="element-494" data-mp="2vjef9lyfnrf5">
                  Unit economics
                </span>
                <span className="text-sm text-slate-400" data-id="element-495" data-mp="30ihj532ofrlj">
                  Fixed Snakker pricing behind the numbers
                </span>
              </span>
              <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${unitsOpen ? 'rotate-180' : ''}`} data-id="element-496" data-mp="2nj8deu7ofpxt" />
            </button>

            <AnimatePresence initial={false} data-id="element-497" data-mp="2xgw8jl972zgi">
              {unitsOpen && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.25
            }} className="overflow-hidden" data-id="element-498" data-mp="2xiz8oy42a8yy">
                  <div className="bg-[#6C2BD9]/[0.07] rounded-2xl p-5 space-y-4 border-2 border-[#6C2BD9]/20" data-id="element-499" data-mp="3q5jz9wcpqh1g">
              {/* Master Snak */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-500" data-mp="cphosupohubj">
                <div className="flex items-center justify-between" data-id="element-501" data-mp="hm87zqws5616">
                  <div className="flex items-center gap-2" data-id="element-502" data-mp="3nrzbufcx5ge">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-503" data-mp="3n8k4lh3cv62">
                      Master Snak
                    </p>
                    <InfoPopover isOpen={infoOpen === 'master'} onToggle={() => setInfoOpen(infoOpen === 'master' ? null : 'master')} onClose={() => setInfoOpen(null)} title="What is a Master Snak?" body="We start by creating a Master Snak presentation — the original parent version of the campaign. All localized versions are derived from this single source of truth." data-id="element-504" data-mp="3q32wu2gqspw9" />
                  </div>
                  <div className="text-right" data-id="element-505" data-mp="3c5ediz2bpqtb">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-506" data-mp="3rll4di4xy0mp">
                      €{Math.round(effectiveMasterSnakCost).toLocaleString()}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-507" data-mp="3ij3fevxwitw0">
                  Per campaign master creation
                </p>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-508" data-mp="3dnwg00bo0bcw">
                  Volume benefit: drops from €2,900 to €1,900 as total campaigns
                  / year scales from 1 to 100+.
                </p>
              </div>

              {/* Cost per country localization */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-509" data-mp="2yub9zihnknot">
                <div className="flex items-center justify-between" data-id="element-510" data-mp="2uszfoznu3eaa">
                  <div className="flex items-center gap-2" data-id="element-511" data-mp="2puo5uoxjh3s2">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-512" data-mp="2kx617aqk1apv">
                      Cost per country localization
                    </p>
                    <InfoPopover isOpen={infoOpen === 'localization'} onToggle={() => setInfoOpen(infoOpen === 'localization' ? null : 'localization')} onClose={() => setInfoOpen(null)} title="What is Localization?" body="Localization adapts the Master version to local usage, language and regulatory requirements — so the campaign is market-ready in every country it ships to." data-id="element-513" data-mp="10ec5yi23bqi2" />
                  </div>
                  <div className="text-right" data-id="element-514" data-mp="vfr0a342kem8">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-515" data-mp="vgkcvkpplyca">
                      €{Math.round(effectiveCountryCost)}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-516" data-mp="2xwx8kthfbil">
                  One-time set-up per country
                </p>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-517" data-mp="36lqq7y77ayo0">
                  Volume benefit: discount slides from 0% at 2 countries up to
                  50% at 50+ countries.
                </p>
              </div>

              {/* Data insights & hosting */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-518" data-mp="3kh2xt66ggjmc">
                <div className="flex items-center justify-between" data-id="element-519" data-mp="2nura7mhooz6v">
                  <div className="flex items-center gap-2" data-id="element-520" data-mp="2rvjk8hydr847">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-521" data-mp="2wtl1qyg5qzdk">
                      Country activation and insights
                    </p>
                    <InfoPopover isOpen={infoOpen === 'insights'} onToggle={() => setInfoOpen(infoOpen === 'insights' ? null : 'insights')} onClose={() => setInfoOpen(null)} title="What are Data Insights?" body="Based on individual HCP engagement, we bi-monthly deliver not only real-world data but also actionable suggestions for improvement — so every campaign keeps getting sharper." data-id="element-522" data-mp="31l32ble9qxy0" />
                  </div>
                  <div className="text-right" data-id="element-523" data-mp="1njole40y0j1o">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-524" data-mp="1nn2m77tykk70">
                      €{effectiveMonthlyInsightsFee.toFixed(0)}
                      <span className="text-xs font-normal text-slate-400 ml-1" data-id="element-525" data-mp="1dtvjjuqlriam">
                        / month
                      </span>
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-526" data-mp="13y1y1luwoegp">Per country</p>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-527" data-mp="zvf51hmi0nhx">
                  Volume benefit: discount slides from 0% at 2 countries up to
                  50% at 50+ countries.
                </p>
              </div>
                  </div>
                </motion.div>}
            </AnimatePresence>

            {/* Portfolio Scale */}
            <div className="bg-[#6C2BD9]/[0.05] rounded-2xl p-5 space-y-5 border border-[#6C2BD9]/15" data-id="element-528" data-mp="baiq51dlw0bn">
              <div data-id="element-529" data-mp="g93qd0suyk03">
                <h3 className="text-xs font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-530" data-mp="g8kcpwa8qhrv">
                  Portfolio Scale
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-531" data-mp="141126rwb5c17">
                  Move the sliders — revenue updates instantly.
                </p>
              </div>
              <SliderInput label="Number of Brands" value={products} onChange={setProducts} min={1} max={40} data-id="element-532" data-mp="u8bv097sikbo" />
              <SliderInput label="Campaigns per Year" value={campaignsPerProduct} onChange={setCampaignsPerProduct} min={2} max={12} data-id="element-533" data-mp="1mw7pr8dsu25t" />
              <SliderInput label="Number of Countries" value={countries} onChange={setCountries} min={1} max={130} data-id="element-534" data-mp="3ccl2ray66hab" />
            </div>
          </div>

          {/* Output */}
          <motion.div key={calc.totalAnnualCost} initial={{
          opacity: 0.7
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-5" data-id="element-535" data-mp="3h665theme1gd">
            {/* Main number */}
            <div className="bg-white rounded-2xl p-6 border-2 border-[#6C2BD9]/20 shadow-[0_0_40px_rgba(108,43,217,0.06)]" data-id="element-536" data-mp="2xhwzm3g8ruxv">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2" data-id="element-537" data-mp="hphli28do78b">
                Cost per Localized Campaign
              </p>
              <p className="text-4xl md:text-5xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-538" data-mp="181mdj2e973pr">
                {formatEuro(calc.costPerDeployment)}
              </p>
              <p className="text-sm text-slate-400 mt-1" data-id="element-539" data-mp="1d17k9yzo0gd1">
                per localized deployment
              </p>
            </div>

            {/* Volume + total annual */}
            <div className="bg-[#6C2BD9]/[0.04] rounded-2xl p-5 border border-[#6C2BD9]/10" data-id="element-540" data-mp="1mzwsf01remg3">
              <p className="text-[11px] font-semibold text-[#6C2BD9] uppercase tracking-widest mb-3" data-id="element-541" data-mp="1qzffo2h1z9m3">
                Campaign Volume
              </p>
              <div className="grid grid-cols-2 gap-4" data-id="element-542" data-mp="1vx77kwhp6vqa">
                <div data-id="element-543" data-mp="20rl5b5ju1ey4">
                  <p className="text-2xl font-bold text-slate-900" data-id="element-544" data-mp="20r1x4gjzi4xa">
                    {calc.totalCampaigns}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-545" data-mp="2aofzxdcziv74">
                    master campaigns / year
                  </p>
                </div>
                <div data-id="element-546" data-mp="2eqkss9vlznsp">
                  <p className="text-2xl font-bold text-[#6C2BD9]" data-id="element-547" data-mp="2enykg80w949f">
                    {calc.totalDeployments}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-548" data-mp="3v5z2b19jks2l">
                    localized deployments / year
                  </p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[#6C2BD9]/10 flex items-center justify-between gap-4 min-h-[36px]" data-id="element-549" data-mp="3l8aub9mxdo2h">
                {costOpen && <p className="text-xs text-slate-500" data-id="element-550" data-mp="mq5l0k7dzckh">Estimated cost</p>}
                <div className="ml-auto flex items-center gap-3" data-id="element-551" data-mp="hswyk7f7gmdf">
                  {costOpen && <motion.p initial={{
                  opacity: 0,
                  y: 4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-552" data-mp="czkmsxvmka23">
                      {formatEuro(calc.totalAnnualCost)}
                      <span className="text-xs text-slate-400 font-normal ml-1" data-id="element-553" data-mp="2v0oa4xobltbc">
                        / year
                      </span>
                    </motion.p>}
                  <button type="button" onClick={() => setCostOpen(v => !v)} aria-pressed={costOpen} aria-label={costOpen ? 'Hide estimated cost' : 'Reveal estimated cost'} title={costOpen ? 'Hide estimated cost' : 'Reveal estimated cost'} className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${costOpen ? 'text-[#6C2BD9] bg-[#6C2BD9]/[0.08]' : 'text-slate-300 hover:text-[#6C2BD9] hover:bg-[#6C2BD9]/[0.08]'}`} data-id="element-554" data-mp="2l6o6z1clmy59">
                    <Euro className="w-3.5 h-3.5" data-id="element-555" data-mp="5gumrwypdrfg" />
                  </button>
                </div>
              </div>
            </div>

            <p className="text-[11px] text-slate-400 leading-relaxed px-1" data-id="element-556" data-mp="am6t1ylcj8se">
              Land with a pilot brand in a few countries, expand across the
              portfolio. Same unit economics at every scale.
            </p>
          </motion.div>
        </div>

        {/* Continue button */}
        <div className="mt-6 flex justify-end" data-id="element-557" data-mp="ybsqagi5rdxr"></div>
      </div>
    </div>;
}